import React from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { enUS, sv, enGB } from 'date-fns/locale';
import { useSelector } from 'react-redux';
import { getTranslate } from 'react-localize-redux';
import { formatCodeDates } from '../../../helpers/functions/functions';
import DATE_FORMATS from '../../../helpers/date_formats';

const localeMap = {
	'en-US': enUS,
	'sv-SE': sv,
	'en-GB': enGB,
};

function normalizeToDate(value) {
	if (!value) return null;

	if (value instanceof Date) return value;


	if (typeof value === 'number') {
		return value.toString().length === 10 ? new Date(value * 1000) : new Date(value);
	}


	if (typeof value === 'string') {

		if (!isNaN(Date.parse(value))) return new Date(value);


		const formatted = value.replace(' ', 'T');
		const date = new Date(formatted);
		if (!isNaN(date.getTime())) return date;

		const utcDate = new Date(formatted + 'Z');
		if (!isNaN(utcDate.getTime())) return utcDate;
	}

	// Fallback: invalid
	return null;
}

function RenderDateTimePicker({ input: { onChange, value }, meta: { invalid, touched, error, active }, ...custom }) {
	const translate = useSelector((state) => getTranslate(state.localize));
	const [errorType, setError] = React.useState(null);

	const warnings = {
		...(custom.inputdata.disablePast && { disablePast: translate('form_field_date_picker_warning_past_date') }),
		...(custom.inputdata.disableFuture && { disableFuture: translate('form_field_date_picker_warning_future_date') }),
	};
  
	const dateValue = normalizeToDate(value);

	return (
		<LocalizationProvider
			dateAdapter={AdapterDateFns}
			adapterLocale={Object.keys(localeMap).includes(custom.locale) ? localeMap[custom.locale] : localeMap['en-US']}
		>
			<DateTimePicker
				name={custom.inputdata.name}
				format={formatCodeDates(custom.locale, DATE_FORMATS.TYPES.DATETIME_PICKERS)}
				label={custom.inputdata.label}
				onError={(newError) => setError(newError)}
				slotProps={{
					textField: {
						fullWidth: true,
						helperText: warnings[errorType],
						InputLabelProps: {
							classes: {
								error: warnings[errorType] && 'datepicker-warning',
							},
						},
						error: touched && invalid && !active,
						helperText: touched && !active && error,
						InputProps: {
							id: custom.inputdata.id,
							classes: {
								error: warnings[errorType] && 'datepicker-warning',
								notchedOutline: warnings[errorType] && 'datepicker-warning',
							},
						},
						FormHelperTextProps: {
							classes: { root: warnings[errorType] && 'datepicker-warning' },
						},
					},
				}}
				inputVariant="outlined"
				onChange={(date) => {
					if (typeof custom.inputdata.onChange !== 'undefined') {
						custom.inputdata.onChange(date);
					}
					onChange(date);
				}}
				value={dateValue}
				ampm={false}
				disableFuture={custom.inputdata.disableFuture}
				disablePast={custom.inputdata.disablePast}
				disabled={custom.inputdata.readonly}
				error={touched && invalid && !active}
				helperText={touched && !active && error}
				openTo={custom.inputdata.openTo}
			/>
		</LocalizationProvider>
	);
}

export default RenderDateTimePicker;
